import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './base.query';
import { WidgetRequestWithFilters } from 'common/models/widget.model';
import { API_PREFIX } from 'common/constants';
import { AggregateResponse } from './chart.api';

export interface PlanningListItem {
  id: number;
  name: string;
  description: string;
  profileCount: number;
  notifications: boolean;
}

export interface PlanningProfileItem {
  id: number;
  name: string;
  conflicts: number;
  events: number;
  interactions: number;
  totalEngagements: number;
  eventCapacity: number;
  profileCountry: string;
  subItems: unknown[];
  interactionItems: unknown[];
}

export interface EventAttendeeItem {
  id: number;
  name: string;
  profileCountry: string;
  conflicts: number;
  status: string;
  Role: string;
  Hours: number;
  fmvRate: number;
  total: number;
}

export const planningApi = createApi({
  reducerPath: 'planningApi',
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getProfilesSummary: builder.query<AggregateResponse, WidgetRequestWithFilters>({
      query: (args) => ({
        url: API_PREFIX.PLANNING + args.request.path,
        headers: args.headers,
        method: 'POST',
        body: {
          request: args.request.data,
          filter: args.filters,
        },
      }),
    }),
    getProfiles: builder.query<PlanningProfileItem[], WidgetRequestWithFilters>({
      query: (args) => ({
        url: API_PREFIX.PLANNING + args.request.path,
        headers: args.headers,
        method: 'POST',
        body: {
          request: args.request.data,
          filter: args.filters,
        },
      }),
    }),
    getProfileDetails: builder.query<PlanningProfileItem[], WidgetRequestWithFilters>({
      query: (args) => ({
        url: API_PREFIX.PLANNING + args.request.path,
        headers: args.headers,
        method: 'POST',
        body: {
          request: args.request.data,
          filter: args.filters,
        },
      }),
    }),
    getProfileDetailsSummary: builder.query<AggregateResponse, WidgetRequestWithFilters>({
      query: (args) => ({
        url: API_PREFIX.PLANNING + args.request.path,
        headers: args.headers,
        method: 'POST',
      }),
    }),
    getProfileActivitiesParticipation: builder.query<
      PlanningProfileItem[],
      WidgetRequestWithFilters
    >({
      query: (args) => ({
        url: API_PREFIX.PLANNING + args.request.path,
        headers: args.headers,
        method: 'POST',
      }),
    }),
    getProfileInteractions: builder.query<PlanningProfileItem[], WidgetRequestWithFilters>({
      query: (args) => ({
        url: API_PREFIX.PLANNING + args.request.path,
        headers: args.headers,
        method: 'POST',
      }),
    }),
    getEventAttendees: builder.query<EventAttendeeItem[], WidgetRequestWithFilters>({
      query: (args) => ({
        url: API_PREFIX.PLANNING + args.request.path,
        headers: args.headers,
        method: 'POST',
        body: {
          request: args.request.data,
          filter: args.filters,
        },
      }),
    }),
    getLists: builder.query<PlanningListItem[], WidgetRequestWithFilters>({
      query: (args) => ({
        url: API_PREFIX.PLANNING + args.request.path,
        headers: args.headers,
        method: 'POST',
        body: {
          request: args.request.data,
          filter: args.filters,
        },
      }),
    }),
  }),
});

export const {
  useGetEventAttendeesQuery,
  useGetProfilesSummaryQuery,
  useGetProfilesQuery,
  useGetProfileDetailsQuery,
  useGetProfileDetailsSummaryQuery,
  useGetProfileActivitiesParticipationQuery,
  useGetProfileInteractionsQuery,
  useGetListsQuery,
} = planningApi;
